import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useApiTokens, useCreateApiToken, useRevokeApiToken, type ApiToken } from "@/api/hooks/apiTokens";
import { errorMessage } from "@/api/errors";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field, FormError } from "@/components/Field";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/dates";

const EXPIRY_CHOICES = [30, 90, 365] as const;

const schema = z.object({
  name: z.string().trim().min(1, "auth.apiTokens.nameRequired").max(60, "auth.apiTokens.nameTooLong"),
  days: z.coerce.number().int().min(1).max(365),
});
type Values = z.input<typeof schema>;

export function ApiTokensCard() {
  const { t } = useTranslation();
  const list = useApiTokens();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();
  const [secret, setSecret] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<ApiToken | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { name: "", days: 90 } });
  const { errors } = form.formState;

  const onSubmit = form.handleSubmit((v) => {
    const parsed = schema.parse(v);
    create.mutate(
      { name: parsed.name, expires_in_days: parsed.days },
      {
        onSuccess: (created) => {
          setSecret(created.token);
          create.reset();
          form.reset();
        },
      },
    );
  });

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      toast.success(t("auth.apiTokens.copied"));
    } catch {
      toast.error(t("auth.apiTokens.copyFailed"));
    }
  };

  const tokens = list.data ?? [];

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl">{t("auth.apiTokens.title")}</h2>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <p className="text-fg-muted">{t("auth.apiTokens.intro")}</p>

        {secret ? (
          <Banner tone="warning" title={t("auth.apiTokens.secretTitle")}>
            <p>{t("auth.apiTokens.secretWarning")}</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <Input readOnly value={secret} aria-label={t("auth.apiTokens.secretLabel")} className="font-mono" />
              <Button type="button" variant="secondary" onClick={copy}>
                {t("auth.apiTokens.copy")}
              </Button>
            </div>
          </Banner>
        ) : null}

        <form noValidate onSubmit={onSubmit} className="flex max-w-md flex-col gap-4">
          <FormError message={create.isError ? errorMessage(create.error, t("errors.generic")) : null} />
          <Field label={t("auth.apiTokens.name")} error={errors.name?.message ? t(errors.name.message) : null}>
            {(p) => <Input {...p} maxLength={60} autoComplete="off" {...form.register("name")} />}
          </Field>
          <Field label={t("auth.apiTokens.expiresIn")}>
            {(p) => (
              <Select {...p} {...form.register("days")}>
                {EXPIRY_CHOICES.map((d) => (
                  <option key={d} value={d}>
                    {t("auth.apiTokens.days", { count: d })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <div>
            <Button type="submit" disabled={create.isPending}>
              {t("auth.apiTokens.create")}
            </Button>
          </div>
        </form>

        {list.isError ? <FormError message={errorMessage(list.error, t("errors.generic"))} /> : null}
        {list.isSuccess && tokens.length === 0 ? <p className="text-fg-muted">{t("auth.apiTokens.empty")}</p> : null}
        {tokens.length > 0 ? (
          <ul className="flex flex-col divide-y divide-border">
            {tokens.map((tok) => (
              <li key={tok.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-heading">{tok.name}</p>
                  <p className="text-sm text-fg-muted">
                    <span className="font-mono">{tok.token_prefix}…</span>
                    {" · "}
                    {t("auth.apiTokens.created", { date: formatDate(tok.created_at) })}
                    {" · "}
                    {t("auth.apiTokens.lastUsed", {
                      date: tok.last_used_at ? formatDate(tok.last_used_at) : t("auth.apiTokens.never"),
                    })}
                    {" · "}
                    {t("auth.apiTokens.expires", { date: formatDate(tok.expires_at) })}
                  </p>
                </div>
                {tok.revoked_at ? (
                  <Badge variant="grey">{t("auth.apiTokens.revoked")}</Badge>
                ) : new Date(tok.expires_at).getTime() <= Date.now() ? (
                  <Badge variant="grey">{t("auth.apiTokens.expired")}</Badge>
                ) : (
                  <Button type="button" variant="secondary" onClick={() => setToRevoke(tok)}>
                    {t("auth.apiTokens.revoke")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
      <ConfirmDialog
        open={toRevoke !== null}
        onOpenChange={(o) => !o && setToRevoke(null)}
        title={t("auth.apiTokens.revokeTitle", { name: toRevoke?.name ?? "" })}
        description={t("auth.apiTokens.revokeBody")}
        confirmLabel={t("auth.apiTokens.revokeConfirm")}
        busy={revoke.isPending}
        onConfirm={() =>
          toRevoke &&
          revoke.mutate(toRevoke.id, {
            onSuccess: () => {
              toast.success(t("auth.apiTokens.revokedToast"));
              setToRevoke(null);
            },
            onError: (e) => {
              toast.error(errorMessage(e, t("errors.generic")));
              setToRevoke(null);
            },
          })
        }
      />
    </Card>
  );
}
